import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import { Dialog, Button } from '@miror/ui-web';

/**
 * Confirmação acessível baseada no `Dialog` do design system, substituindo
 * `window.confirm`. Promise-based: `const ok = await confirm({ ... })`.
 *
 * Monte `<ConfirmProvider>` uma vez no root; use `useConfirm()` nas páginas.
 */
export interface ConfirmOptions {
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** `danger` usa o botão destrutivo (vermelho). Default: `default`. */
  tone?: 'default' | 'danger';
}

type ConfirmFn = (opts: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

interface PendingState extends ConfirmOptions {
  open: boolean;
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<PendingState>({ open: false, title: '' });
  const resolverRef = useRef<((value: boolean) => void) | null>(null);

  const confirm = useCallback<ConfirmFn>((opts) => {
    setState({ ...opts, open: true });
    return new Promise<boolean>((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  const settle = useCallback((value: boolean) => {
    resolverRef.current?.(value);
    resolverRef.current = null;
    setState((s) => ({ ...s, open: false }));
  }, []);

  const value = useMemo(() => confirm, [confirm]);

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      <Dialog
        open={state.open}
        onClose={() => settle(false)}
        title={state.title}
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => settle(false)}>
              {state.cancelLabel ?? 'Cancelar'}
            </Button>
            <Button
              variant={state.tone === 'danger' ? 'destructive' : 'default'}
              onClick={() => settle(true)}
            >
              {state.confirmLabel ?? 'Confirmar'}
            </Button>
          </>
        }
      >
        <p className="text-sm text-muted-foreground">
          {state.description ?? 'Esta ação não pode ser desfeita.'}
        </p>
      </Dialog>
    </ConfirmContext.Provider>
  );
}

/** Retorna a função de confirmação. Lança se usada fora do provider. */
export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm precisa de <ConfirmProvider> na árvore.');
  return ctx;
}
